/**
 * Operations Domain Store
 *
 * Zustand-based store for the operations propose → review → apply surface
 * (TV1-1828). Wires the pure handlers + invariants; policies (run subscription,
 * change-set load, approval/reject submission, run-history query) are created
 * with the backend dependency and attached via `useOperationsPolicies`.
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { OperationsCommand } from './commands';
import { Policy } from '@commandDrivenStateEngine/core/policies';
import { OperationsState } from './state';
import { createOperationRunPolicy } from './policies';

const {
  useSelector,
  useErrors,
  usePolicies,
  getState,
  dispatch
} = createStore({
  name: 'operations',
  initialState,
  handlers,
  invariants
});

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. Mirrors the warehouses
 * dispatch hook. An explicit `meta.user` (e.g. from tests) is left untouched.
 */
const useOperationsDispatch = (): Dispatch<OperationsCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<OperationsCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory — creates the operations policies with the backend dependency.
 *
 * The review surface always targets a single `OperationRun`, so the run policy
 * (lifecycle subscription + change-set load + approve/reject submission + apply
 * progress) needs the `runId`. A parent review page passes the `runId` from the
 * route; the factory returns the run policy only when a `runId` is present, so
 * `useOperationsPolicies` activates it. Other backend-only policies (e.g.
 * run-history query) can be appended here as the domain grows.
 */
export const createOperationsPolicies = (
  backend: ClientLibraryType,
  runId?: string
): Policy<OperationsState, OperationsCommand>[] =>
  runId ? [createOperationRunPolicy( backend, runId )] : [];

// Export hooks with domain-specific names.
export {
  useSelector as useOperationsSelector,
  useErrors as useOperationsErrors,
  useOperationsDispatch,
  usePolicies as useOperationsPolicies,
  getState as getOperationsState,
  dispatch as dispatchOperationsCommand
};

export { selectors } from './state';
